import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  BookOpen, Users, Search, Filter, Eye, FileText, TrendingUp,
  BarChart3, PieChart, User, CheckCircle, AlertCircle, X,
  ChevronLeft, ChevronRight, GraduationCap, Trash2,
  Upload, Image, RefreshCw, ClipboardList, MessageSquare, Calendar, Download, Book
} from 'lucide-react';
import {
  fetchHPCStats, fetchHPCClasses, fetchClassStudents, fetchAllStudents,
  fetchStudentActivities, generateHPCReport, fetchGeneratedReports,
  fetchReportById, deleteReport, fetchSchoolBranding, updateSchoolBranding,
  uploadSchoolLogo, uploadPrincipalSignature, fetchHPCCoverage,
  fetchStudentProfilePicture, displayDate,
  fetchAssignmentDetail, fetchAssignmentSubmissions,
  fetchTeacherStats, fetchTeacherDetail, fetchTeachersList,
  fetchManagementSchools,
  generateBulkHPCReports, getBulkGenerationStatus,
  fetchClassActivities,
} from '../../Components/HPC_Report/services/adminHPCService';
import AdminHPCReportViewer from '../../Components/HPC_Report/AdminHPCReportViewer';
import HPCAssignmentDetailView from '../domain-assignment/HPCAssignmentDetailView';
import { API_BASE_URL } from '../../config';

const HPCManagement = () => {
  // ─── Role detection ───────────────────────────────────────────────────────
  const userRole = localStorage.getItem('user_role');
  const userSchoolId = localStorage.getItem('school_id');
  const isManagement = userRole === 'management';

  // ─── State ──────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Data
  const [stats, setStats] = useState(null);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [totalStudents, setTotalStudents] = useState(0);
  const [activities, setActivities] = useState([]);
  const [reports, setReports] = useState([]);
  const [totalReports, setTotalReports] = useState(0);
  const [coverage, setCoverage] = useState(null);
  const [branding, setBranding] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [totalTeachers, setTotalTeachers] = useState(0);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teacherDetail, setTeacherDetail] = useState(null);

  // Selection / detail state
  const [selectedClass, setSelectedClass] = useState(null);
  const [classStudents, setClassStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [viewingReport, setViewingReport] = useState(null);
  const [studentImage, setStudentImage] = useState(null);

  // Filters
  const [classFilters, setClassFilters] = useState({ stage: '', search: '', grade: '', teacher_id: '' });
  const [allTeachersList, setAllTeachersList] = useState([]);
  const [studentPage, setStudentPage] = useState(1);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentStageFilter, setStudentStageFilter] = useState('');
  const [studentReportFilter, setStudentReportFilter] = useState('');
  const [studentGradeFilter, setStudentGradeFilter] = useState('');
  const [reportPage, setReportPage] = useState(1);
  const [reportSearch, setReportSearch] = useState('');
  const [reportStageFilter, setReportStageFilter] = useState('');
  const [reportGradeFilter, setReportGradeFilter] = useState('');
  const [reportGeneratedByFilter, setReportGeneratedByFilter] = useState('');
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  const [coverageTeacherFilter, setCoverageTeacherFilter] = useState('');
  const [teacherPage, setTeacherPage] = useState(1);
  const [teacherSearch, setTeacherSearch] = useState('');

  // Activity-level filters (used in the activity selection view)
  const [activityStageFilter, setActivityStageFilter] = useState('');
  const [activityDomainFilter, setActivityDomainFilter] = useState('');
  const [activityClassFilter, setActivityClassFilter] = useState('');
  const [activityTeacherFilter, setActivityTeacherFilter] = useState('');

  // Branding form
  const [brandingForm, setBrandingForm] = useState({
    principal_name: '', principal_designation: '',
    school_name: '', school_address: '', academic_year: '',
  });

  // Generating state
  const [generating, setGenerating] = useState(false);
  const PAGE_SIZE = 20;

  // Bulk generation state
  const [bulkJobId, setBulkJobId] = useState(null);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isBulkGenerating, setIsBulkGenerating] = useState(false);

  // Class-level activities (for class Activities sub-tab)
  const [classTab, setClassTab] = useState('students'); // 'students' | 'activities'
  const [classActivities, setClassActivities] = useState([]);
  const [classActivitiesLoading, setClassActivitiesLoading] = useState(false);
  const [classActivitySelected, setClassActivitySelected] = useState([]); // selected activity IDs in class tab
  const [classActivityDomainFilter, setClassActivityDomainFilter] = useState('');
  const [classActivityStageFilter, setClassActivityStageFilter] = useState('');

  // ─── Management: school selection ────────────────────────────────────────
  const [selectedSchool, setSelectedSchool] = useState(null);      // { school_id, school_name, ... }
  const [managementSchools, setManagementSchools] = useState([]);
  const [managementSchoolsLoading, setManagementSchoolsLoading] = useState(false);

  // Drill-down state for assignment detail & submissions
  const [viewingAssignment, setViewingAssignment] = useState(null);
  const [assignmentSubmissions, setAssignmentSubmissions] = useState([]);
  const [viewingSubmission, setViewingSubmission] = useState(null);
  const [drillDownLoading, setDrillDownLoading] = useState(false);

  // ─── Notifications ────────────────────────────────────────────────────────
  const showNotification = (type, message) => {
    if (type === 'success') {
      setSuccess(message);
      setTimeout(() => setSuccess(null), message.length > 90 ? 10000 : 3000);
    } else {
      setError(message);
      setTimeout(() => setError(null), 5000);
    }
  };

  // ─── Data Fetching ────────────────────────────────────────────────────────
  const loadStats = useCallback(async () => {
    try {
      const data = await fetchHPCStats(selectedSchool?.school_id || null);
      setStats(data);
    } catch (err) {
      showNotification('error', `Failed to load stats: ${err.message}`);
    }
  }, [selectedSchool]);

  const loadClasses = useCallback(async () => {
    try {
      const params = {};
      if (classFilters.stage) params.stage = classFilters.stage;
      if (classFilters.search) params.search = classFilters.search;
      if (classFilters.grade) params.grade = classFilters.grade;
      if (classFilters.teacher_id) params.teacher_id = classFilters.teacher_id;
      const data = await fetchHPCClasses(params, selectedSchool?.school_id || null);
      setClasses(data.classes || data || []);
    } catch (err) {
      showNotification('error', `Failed to load classes: ${err.message}`);
    }
  }, [classFilters, selectedSchool]);

  const loadStudents = useCallback(async () => {
    try {
      const params = { page: studentPage, limit: PAGE_SIZE };
      if (studentSearch) params.search = studentSearch;
      if (studentStageFilter) params.stage = studentStageFilter;
      if (studentReportFilter) params.has_report = studentReportFilter;
      const data = await fetchAllStudents(params, selectedSchool?.school_id || null);
      setStudents(data.students || []);
      setTotalStudents(data.total || 0);
    } catch (err) {
      showNotification('error', `Failed to load students: ${err.message}`);
    }
  }, [studentPage, studentSearch, studentStageFilter, studentReportFilter, selectedSchool]);

  const loadReports = useCallback(async () => {
    try {
      const params = { page: reportPage, limit: PAGE_SIZE };
      if (reportSearch) params.search = reportSearch;
      if (reportStageFilter) params.stage = reportStageFilter;
      const data = await fetchGeneratedReports(params, selectedSchool?.school_id || null);
      setReports(data.reports || []);
      setTotalReports(data.total || 0);
    } catch (err) {
      showNotification('error', `Failed to load reports: ${err.message}`);
    }
  }, [reportPage, reportSearch, reportStageFilter, selectedSchool]);

  const loadCoverage = useCallback(async () => {
    try {
      const data = await fetchHPCCoverage({}, selectedSchool?.school_id || null);
      setCoverage(data);
    } catch (err) {
      showNotification('error', `Failed to load coverage: ${err.message}`);
    }
  }, [selectedSchool]);

  const loadBranding = useCallback(async () => {
    try {
      const data = await fetchSchoolBranding(selectedSchool?.school_id || null);
      setBranding(data);
      setBrandingForm({
        principal_name: data.principal_name || '',
        principal_designation: data.principal_designation || '',
        school_name: data.school_name || '',
        school_address: data.school_address || '',
        academic_year: data.academic_year || '',
      });
    } catch (err) {
      showNotification('error', `Failed to load branding: ${err.message}`);
    }
  }, [selectedSchool]);

  const loadTeachers = useCallback(async () => {
    try {
      const params = { page: teacherPage, limit: PAGE_SIZE };
      if (teacherSearch) params.search = teacherSearch;
      const data = await fetchTeacherStats(params, selectedSchool?.school_id || null);
      setTeachers(data.teachers || []);
      setTotalTeachers(data.total || 0);
    } catch (err) {
      showNotification('error', `Failed to load teachers: ${err.message}`);
    }
  }, [teacherPage, teacherSearch, selectedSchool]);

  const loadTeacherDetail = useCallback(async (teacherId) => {
    try {
      const data = await fetchTeacherDetail(teacherId, selectedSchool?.school_id || null);
      setTeacherDetail(data);
    } catch (err) {
      showNotification('error', `Failed to load teacher detail: ${err.message}`);
    }
  }, [selectedSchool]);

  // ─── Management: load schools list ───────────────────────────────────────
  const loadManagementSchools = useCallback(async () => {
    if (!isManagement) return;
    setManagementSchoolsLoading(true);
    try {
      const data = await fetchManagementSchools();
      setManagementSchools(data.schools || []);
    } catch (err) {
      showNotification('error', `Failed to load schools: ${err.message}`);
    } finally {
      setManagementSchoolsLoading(false);
    }
  }, [isManagement]);

  // ─── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isManagement) loadManagementSchools();
  }, [isManagement, loadManagementSchools]);

  // When management drills into a school, reset drill-down state and switch to overview
  useEffect(() => {
    if (isManagement && selectedSchool) {
      setActiveTab('overview');
      setSelectedClass(null);
      setClassStudents([]);
      setSelectedStudent(null);
      setActivities([]);
      setSelectedActivities([]);
      setViewingAssignment(null);
      setAssignmentSubmissions([]);
      setViewingSubmission(null);
      setCoverage(null);
      setBranding(null);
    }
  }, [isManagement, selectedSchool]);

  useEffect(() => {
    setLoading(true);
    // For management without a selected school, skip the initial load
    if (isManagement && !selectedSchool) {
      setLoading(false);
      return;
    }
    Promise.all([loadStats(), loadCoverage()]).finally(() => setLoading(false));
  }, [loadStats, loadCoverage]);

  useEffect(() => {
    if (activeTab === 'classes') {
      loadClasses();
      // Load teachers list for the filter dropdown
      fetchTeachersList(selectedSchool?.school_id || null).then(setAllTeachersList).catch(() => {});
    }
  }, [activeTab, loadClasses, selectedSchool]);

  useEffect(() => {
    if (activeTab === 'students') loadStudents();
  }, [activeTab, loadStudents]);

  useEffect(() => {
    if (activeTab === 'reports') loadReports();
  }, [activeTab, loadReports]);

  useEffect(() => {
    if (activeTab === 'coverage') loadCoverage();
  }, [activeTab, loadCoverage]);

  useEffect(() => {
    if (activeTab === 'branding') loadBranding();
  }, [activeTab, loadBranding]);

  useEffect(() => {
    if (activeTab === 'teachers') loadTeachers();
  }, [activeTab, loadTeachers]);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleClassClick = async (cls) => {
    setSelectedClass(cls);
    setSelectedStudent(null);
    setActivities([]);
    setSelectedActivities([]);
    setClassTab('students');
    setClassActivities([]);
    setClassActivitySelected([]);
    setClassActivityDomainFilter('');
    setClassActivityStageFilter('');
    try {
      const data = await fetchClassStudents(cls.id, selectedSchool?.school_id || null);
      setClassStudents(data.students || data || []);
    } catch (err) {
      showNotification('error', `Failed to load class students: ${err.message}`);
    }
  };

  const handleLoadClassActivities = async () => {
    if (!selectedClass) return;
    setClassActivitiesLoading(true);
    try {
      const params = {};
      if (classActivityDomainFilter) params.domain = classActivityDomainFilter;
      const data = await fetchClassActivities(selectedClass.id, params, selectedSchool?.school_id || null);
      setClassActivities(Array.isArray(data) ? data : []);
    } catch (err) {
      showNotification('error', `Failed to load class activities: ${err.message}`);
    } finally {
      setClassActivitiesLoading(false);
    }
  };

  const handleBulkGenerateFromClassTab = async () => {
    if (!selectedClass || classActivitySelected.length === 0) {
      showNotification('error', 'Please select at least one activity');
      return;
    }
    const selectedActs = classActivities.filter(a => classActivitySelected.includes(a.id));
    const stages = [...new Set(selectedActs.map(a => a.stage).filter(Boolean))];
    if (stages.length > 1) {
      showNotification('error', `Cannot mix stages in one report. Selected activities span: ${stages.join(', ')}.`);
      return;
    }
    setIsBulkGenerating(true);
    try {
      const result = await generateBulkHPCReports(selectedClass.id, classActivitySelected, true);
      setBulkJobId(result.job_id);
      setBulkProgress({ ...result, status: 'in_progress', completed: 0, failed: 0, results: [] });
      setIsBulkModalOpen(true);
    } catch (err) {
      showNotification('error', `Failed to start bulk generation: ${err.message}`);
      setIsBulkGenerating(false);
    }
  };

  const handleStudentClick = async (student) => {
    setSelectedStudent(student);
    setSelectedActivities([]);
    setActivityStageFilter('');
    setActivityDomainFilter('');
    setActivityClassFilter('');
    try {
      const data = await fetchStudentActivities(student.id, {}, selectedSchool?.school_id || null);
      setActivities(data.activities || data || []);
    } catch (err) {
      showNotification('error', `Failed to load activities: ${err.message}`);
    }
  };

  const handleActivityToggle = (activityId) => {
    setSelectedActivities(prev =>
      prev.includes(activityId)
        ? prev.filter(id => id !== activityId)
        : [...prev, activityId]
    );
  };

  // Compute filtered activities based on activity-level filters
  const filteredActivities = activities.filter(act => {
    if (activityStageFilter && act.stage !== activityStageFilter) return false;
    if (activityDomainFilter && act.domain !== activityDomainFilter) return false;
    if (activityClassFilter && act.class_id !== activityClassFilter) return false;
    if (activityTeacherFilter && act.teacher_name !== activityTeacherFilter) return false;
    return true;
  });

  // Derive unique domains and classes from loaded activities for filter dropdowns
  const activityDomains = [...new Set(activities.map(a => a.domain).filter(Boolean))].sort();
  const activityClasses = [...new Map(activities.map(a => [a.class_id, { id: a.class_id, name: a.class_name }]).filter(([id]) => id)).values()];
  const activityStages = [...new Set(activities.map(a => a.stage).filter(Boolean))].sort();
  const activityTeachers = [...new Set(activities.map(a => a.teacher_name).filter(Boolean))].sort();

  const handleSelectAllActivities = () => {
    const visibleIds = filteredActivities.map(a => a.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedActivities.includes(id));
    if (allVisibleSelected) {
      // Deselect only the visible ones
      setSelectedActivities(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      // Select all visible ones (merge with existing)
      setSelectedActivities(prev => [...new Set([...prev, ...visibleIds])]);
    }
  };

  const handleGenerateReport = async () => {
    if (!selectedStudent || selectedActivities.length === 0) {
      showNotification('error', 'Please select a student and at least one activity');
      return;
    }
    // Validate all selected activities are from the same stage
    const selectedActs = activities.filter(a => selectedActivities.includes(a.id));
    const stages = [...new Set(selectedActs.map(a => a.stage).filter(Boolean))];
    if (stages.length > 1) {
      showNotification('error', `Cannot mix stages in one report. Selected activities span: ${stages.join(', ')}. Please filter by a single stage and select activities from that stage only.`);
      return;
    }
    setGenerating(true);
    try {
      const generated = await generateHPCReport(selectedStudent.id, selectedActivities, true);
      // Approved is not published: say what the student's report left out, in the same message
      const leftOut = generated && generated.warnings && generated.warnings.length ? ` ${generated.warnings.join(' ')}` : '';
      showNotification('success', `Report generated for ${selectedStudent.name}.${leftOut}`);
      setSelectedActivities([]);
      loadStats();
      if (activeTab === 'reports') loadReports();
    } catch (err) {
      showNotification('error', `Failed to generate report: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleBulkGenerateReports = async () => {
    if (!selectedClass || selectedActivities.length === 0) {
      showNotification('error', 'Please select a class and at least one activity');
      return;
    }
    const selectedActs = activities.filter(a => selectedActivities.includes(a.id));
    const stages = [...new Set(selectedActs.map(a => a.stage).filter(Boolean))];
    if (stages.length > 1) {
      showNotification('error', `Cannot mix stages in one report. Selected activities span: ${stages.join(', ')}.`);
      return;
    }
    setIsBulkGenerating(true);
    try {
      const result = await generateBulkHPCReports(selectedClass.id, selectedActivities, true);
      setBulkJobId(result.job_id);
      setBulkProgress({ ...result, status: 'in_progress', completed: 0, failed: 0, results: [] });
      setIsBulkModalOpen(true);
    } catch (err) {
      showNotification('error', `Failed to start bulk generation: ${err.message}`);
      setIsBulkGenerating(false);
    }
  };

  // Poll bulk job status while modal is open and job is in progress
  useEffect(() => {
    if (!bulkJobId || !isBulkModalOpen) return;
    if (bulkProgress?.status === 'done') return;

    let cancelled = false;

    const poll = async () => {
      try {
        const status = await getBulkGenerationStatus(bulkJobId);
        if (cancelled) return;
        setBulkProgress(status);
        if (status.status === 'done') {
          setIsBulkGenerating(false);
          loadStats();
        }
      } catch {
        if (!cancelled) setIsBulkGenerating(false);
      }
    };

    // Immediate first poll so progress shows without waiting 1.5 s
    poll();
    const interval = setInterval(poll, 1500);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [bulkJobId, isBulkModalOpen, bulkProgress?.status]);

  const handleCloseBulkModal = () => {
    if (isBulkGenerating) return;
    setIsBulkModalOpen(false);
    setBulkJobId(null);
    setBulkProgress(null);
    setSelectedActivities([]);
    setClassActivitySelected([]);
  };

  const handleViewReport = async (reportId) => {
    try {
      setLoading(true);
      const report = await fetchReportById(reportId, selectedSchool?.school_id || null);
      const img = await fetchStudentProfilePicture(report.student_id);
      setStudentImage(img);
      setViewingReport(report);
    } catch (err) {
      showNotification('error', `Failed to load report: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    try {
      await deleteReport(reportId);
      showNotification('success', 'Report deleted');
      loadReports();
      loadStats();
    } catch (err) {
      showNotification('error', `Failed to delete report: ${err.message}`);
    }
  };

  const handleBrandingSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateSchoolBranding(brandingForm);
      showNotification('success', 'Branding updated');
      loadBranding();
    } catch (err) {
      showNotification('error', `Failed to update branding: ${err.message}`);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Reset input so the same file can be re-selected after an error
    e.target.value = '';
    try {
      await uploadSchoolLogo(file, selectedSchool?.school_id || null);
      showNotification('success', 'Logo uploaded successfully');
      loadBranding();
    } catch (err) {
      showNotification('error', `Failed to upload logo: ${err.message}`);
    }
  };

  const handleSignatureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Reset input so the same file can be re-selected after an error
    e.target.value = '';
    try {
      await uploadPrincipalSignature(file, selectedSchool?.school_id || null);
      showNotification('success', 'Signature uploaded successfully');
      loadBranding();
    } catch (err) {
      showNotification('error', `Failed to upload signature: ${err.message}`);
    }
  };

  const handleBackFromClass = () => {
    setSelectedClass(null);
    setClassStudents([]);
    setSelectedStudent(null);
    setActivities([]);
    setSelectedActivities([]);
    setActivityStageFilter('');
    setActivityDomainFilter('');
    setActivityClassFilter('');
  };

  const handleBackFromStudent = () => {
    setSelectedStudent(null);
    setActivities([]);
    setSelectedActivities([]);
    setActivityStageFilter('');
    setActivityDomainFilter('');
    setActivityClassFilter('');
  };

  // ─── Drill-down handlers ──────────────────────────────────────────────────
  const handleViewAssignment = async (activityId) => {
    setDrillDownLoading(true);
    try {
      const detail = await fetchAssignmentDetail(activityId, selectedSchool?.school_id || null);
      setViewingAssignment(detail);
      setViewingSubmission(null);
      setAssignmentSubmissions([]);
    } catch (err) {
      showNotification('error', `Failed to load assignment: ${err.message}`);
    } finally {
      setDrillDownLoading(false);
    }
  };

  const handleViewSubmissions = async (assignmentId) => {
    setDrillDownLoading(true);
    try {
      const data = await fetchAssignmentSubmissions(assignmentId, selectedSchool?.school_id || null);
      setAssignmentSubmissions(data.submissions || []);
    } catch (err) {
      showNotification('error', `Failed to load submissions: ${err.message}`);
    } finally {
      setDrillDownLoading(false);
    }
  };

  const handleViewSubmissionDetail = (submission) => {
    setViewingSubmission(submission);
  };

  const handleBackFromAssignment = () => {
    setViewingAssignment(null);
    setAssignmentSubmissions([]);
    setViewingSubmission(null);
  };

  const handleBackFromSubmissions = () => {
    setAssignmentSubmissions([]);
    setViewingSubmission(null);
  };

  const handleBackFromSubmissionDetail = () => {
    setViewingSubmission(null);
  };

  // ─── If viewing a report, render the viewer ───────────────────────────────
  if (viewingReport) {
    return (
      <>
        <AdminHPCReportViewer
          reportData={viewingReport.report_data}
          stage={viewingReport.stage}
          studentImage={studentImage}
          schoolBranding={viewingReport.school_branding || branding}
          onClose={() => setViewingReport(null)}
        />
      </>
    );
  }

  // ─── Tab definitions ──────────────────────────────────────────────────────
  const tabs = [
    { key: 'overview', label: 'Overview', icon: <BarChart3 className="w-4 h-4" /> },
    { key: 'teachers', label: 'Teachers', icon: <User className="w-4 h-4" /> },
    { key: 'classes', label: 'Classes', icon: <GraduationCap className="w-4 h-4" /> },
    { key: 'students', label: 'Students', icon: <Users className="w-4 h-4" /> },
    { key: 'reports', label: 'Reports', icon: <FileText className="w-4 h-4" /> },
    { key: 'branding', label: 'Branding', icon: <Image className="w-4 h-4" /> },
    { key: 'coverage', label: 'Coverage', icon: <ClipboardList className="w-4 h-4" /> },
  ];

  // ─── Render helpers ───────────────────────────────────────────────────────
  const renderStageTag = (stage) => {
    const colors = {
      'Foundational Stage': 'bg-blue-100 text-blue-800',
      'Preparatory Stage': 'bg-green-100 text-green-800',
      'Middle Stage': 'bg-purple-100 text-purple-800',
      'Secondary Stage': 'bg-orange-100 text-orange-800',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[stage] || 'bg-gray-100 text-gray-800'}`}>
        {stage || 'Unknown'}
      </span>
    );
  };

  const renderPagination = (currentPage, setPage, total) => {
    const totalPages = Math.ceil(total / PAGE_SIZE);
    if (totalPages <= 1) return null;
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, total);
    return (
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {startItem} to {endItem} of {total} results
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum ? 'bg-[#D67C2C] text-white' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Overview Tab ─────────────────────────────────────────────────────────
  const renderOverview = () => (
    <>
      {/* Quick Stats Section */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <BarChart3 className="w-6 h-6 text-[#D67C2C]" />
          <h2 className="text-2xl font-bold text-gray-900">Quick Statistics</h2>
        </div>
        <p className="text-gray-600 mb-6">Overview of HPC activities and reports across your institution</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl shadow-md overflow-hidden border border-blue-200 transition-all duration-300 hover:shadow-xl hover:scale-105">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-white shadow-sm">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-blue-700">{stats?.total_activities || 0}</p>
              </div>
            </div>
            <h3 className="text-sm font-semibold text-blue-900 mb-1">Total Activities</h3>
            <p className="text-xs text-blue-700">Activities created for HPC</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl shadow-md overflow-hidden border border-green-200 transition-all duration-300 hover:shadow-xl hover:scale-105">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-white shadow-sm">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-green-700">{stats?.total_submissions || 0}</p>
              </div>
            </div>
            <h3 className="text-sm font-semibold text-green-900 mb-1">Total Submissions</h3>
            <p className="text-xs text-green-700">Student work submitted</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl shadow-md overflow-hidden border border-purple-200 transition-all duration-300 hover:shadow-xl hover:scale-105">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-white shadow-sm">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-purple-700">{stats?.reports_generated || 0}</p>
              </div>
            </div>
            <h3 className="text-sm font-semibold text-purple-900 mb-1">Reports Generated</h3>
            <p className="text-xs text-purple-700">Official HPC reports created</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl shadow-md overflow-hidden border border-orange-200 transition-all duration-300 hover:shadow-xl hover:scale-105">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-white shadow-sm">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-orange-700">{stats?.students_without_reports || 0}</p>
              </div>
            </div>
            <h3 className="text-sm font-semibold text-orange-900 mb-1">Pending Reports</h3>
            <p className="text-xs text-orange-700">Students without reports</p>
          </div>
        </div>
      </div>

      {/* Feedback Profiling Section */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <MessageSquare className="w-6 h-6 text-[#D67C2C]" />
          <h2 className="text-2xl font-bold text-gray-900">Feedback Profiling</h2>
        </div>
        <p className="text-gray-600 mb-6">Breakdown of feedback submissions by type</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-2xl shadow-md overflow-hidden border border-cyan-200 transition-all duration-300 hover:shadow-xl hover:scale-105">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-white shadow-sm">
                <User className="w-6 h-6 text-cyan-600" />
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-cyan-700">{stats?.self_assessment_count || 0}</p>
              </div>
            </div>
            <h3 className="text-sm font-semibold text-cyan-900 mb-1">Self Assessments</h3>
            <p className="text-xs text-cyan-700">Student self-evaluations</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl shadow-md overflow-hidden border border-indigo-200 transition-all duration-300 hover:shadow-xl hover:scale-105">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-white shadow-sm">
                <Users className="w-6 h-6 text-indigo-600" />
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-indigo-700">{stats?.peer_assessment_count || 0}</p>
              </div>
            </div>
            <h3 className="text-sm font-semibold text-indigo-900 mb-1">Peer Assessments</h3>
            <p className="text-xs text-indigo-700">Peer-to-peer evaluations</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-2xl shadow-md overflow-hidden border border-pink-200 transition-all duration-300 hover:shadow-xl hover:scale-105">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-white shadow-sm">
                <Eye className="w-6 h-6 text-pink-600" />
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-pink-700">{stats?.caregiver_observation_count || 0}</p>
              </div>
            </div>
            <h3 className="text-sm font-semibold text-pink-900 mb-1">Caregiver Observations</h3>
            <p className="text-xs text-pink-700">Parent/guardian feedback</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl shadow-md overflow-hidden border border-teal-200 transition-all duration-300 hover:shadow-xl hover:scale-105">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-white shadow-sm">
                <GraduationCap className="w-6 h-6 text-teal-600" />
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-teal-700">{stats?.teacher_feedback_count || 0}</p>
              </div>
            </div>
            <h3 className="text-sm font-semibold text-teal-900 mb-1">Teacher Feedback</h3>
            <p className="text-xs text-teal-700">Teacher evaluations & rubrics</p>
          </div>
        </div>
      </div>

      {/* Part A/B/C/D Breakdown by Stage */}
      {stats?.parts_by_stage && Object.keys(stats.parts_by_stage).length > 0 && (
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <ClipboardList className="w-6 h-6 text-[#D67C2C]" />
            <h2 className="text-2xl font-bold text-gray-900">Submission Parts by Stage</h2>
          </div>
          <p className="text-gray-600 mb-6">Part A (Student Work) · Part B (Teacher Feedback) · Part C (Self/Peer Assessment) · Part D (Caregiver Observation)</p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {Object.entries(stats.parts_by_stage).map(([stage, parts]) => {
              const stageColors = {
                'Foundational Stage': { bg: 'from-blue-50 to-blue-100', border: 'border-blue-200', text: 'text-blue-700', bar: 'bg-blue-500' },
                'Preparatory Stage': { bg: 'from-green-50 to-green-100', border: 'border-green-200', text: 'text-green-700', bar: 'bg-green-500' },
                'Middle Stage': { bg: 'from-purple-50 to-purple-100', border: 'border-purple-200', text: 'text-purple-700', bar: 'bg-purple-500' },
              };
              const colors = stageColors[stage] || { bg: 'from-gray-50 to-gray-100', border: 'border-gray-200', text: 'text-gray-700', bar: 'bg-gray-500' };
              const maxVal = Math.max(parts.part_a || 0, parts.part_b || 0, parts.part_c || 0, parts.part_d || 0, 1);

              return (
                <div key={stage} className={`bg-gradient-to-br ${colors.bg} rounded-2xl shadow-md overflow-hidden ${colors.border} border p-6`}>
                  <h3 className={`text-lg font-semibold ${colors.text} mb-4`}>{stage}</h3>
                  <div className="space-y-3">
                    {[
                      { label: 'Part A — Student Work', value: parts.part_a || 0 },
                      { label: 'Part B — Teacher Feedback', value: parts.part_b || 0 },
                      { label: 'Part C — Self/Peer Assessment', value: parts.part_c || 0 },
                      { label: 'Part D — Caregiver Observation', value: parts.part_d || 0 },
                    ].map((item, idx) => (
                      <div key={idx} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-600">{item.label}</span>
                          <span className="text-sm font-bold text-gray-900">{item.value}</span>
                        </div>
                        <div className="w-full bg-white/60 rounded-full h-2">
                          <div
                            className={`${colors.bar} h-2 rounded-full transition-all duration-500`}
                            style={{ width: `${Math.round((item.value / maxVal) * 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Analytics Section */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <PieChart className="w-6 h-6 text-[#D67C2C]" />
          <h2 className="text-2xl font-bold text-gray-900">Detailed Analytics</h2>
        </div>
        <p className="text-gray-600 mb-6">Breakdown of activities and submissions by stage and domain</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Activities by Stage */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-blue-100 to-blue-200">
              <PieChart className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Activities by Stage</h3>
              <p className="text-xs text-gray-500">Distribution across educational levels</p>
            </div>
          </div>
          <div className="space-y-3">
            {stats?.activities_by_stage && Object.keys(stats.activities_by_stage).length > 0 ? (
              Object.entries(stats.activities_by_stage).map(([stage, count], index) => {
                const total = Object.values(stats.activities_by_stage).reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <div key={index} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">{stage}</span>
                      <span className="text-sm font-bold text-gray-900">{count}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No activities yet</p>
                <p className="text-xs text-gray-400 mt-1">Activities will appear here once created</p>
              </div>
            )}
          </div>
        </div>

        {/* Activities by Domain */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-green-100 to-green-200">
              <BarChart3 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Activities by Domain</h3>
              <p className="text-xs text-gray-500">Top 5 subject areas</p>
            </div>
          </div>
          <div className="space-y-3">
            {stats?.activities_by_domain && Object.keys(stats.activities_by_domain).length > 0 ? (
              Object.entries(stats.activities_by_domain).slice(0, 5).map(([domain, count], index) => {
                const colors = ['from-green-500 to-green-600', 'from-blue-500 to-blue-600', 'from-purple-500 to-purple-600', 'from-orange-500 to-orange-600', 'from-pink-500 to-pink-600'];
                return (
                  <div key={index} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colors[index % colors.length]} flex items-center justify-center text-white text-xs font-bold`}>
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 truncate">{domain}</p>
                    </div>
                    <span className="text-sm font-bold text-gray-900 bg-gray-100 px-3 py-1 rounded-full">{count}</span>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <Book className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No domain data yet</p>
                <p className="text-xs text-gray-400 mt-1">Domain breakdown will appear here</p>
              </div>
            )}
          </div>
        </div>

        {/* Top Teachers */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-purple-100 to-purple-200">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Top Contributors</h3>
              <p className="text-xs text-gray-500">Teachers with most activities</p>
            </div>
          </div>
          <div className="space-y-3">
            {stats?.top_teachers?.length > 0 ? (
              stats.top_teachers.slice(0, 5).map((teacher, index) => {
                const medals = ['🥇', '🥈', '🥉'];
                return (
                  <div key={index} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="text-2xl">{medals[index] || '👤'}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 truncate">{teacher.name || teacher.teacher_name}</p>
                    </div>
                    <span className="text-sm font-bold text-gray-900 bg-purple-100 px-3 py-1 rounded-full">{teacher.count || teacher.activity_count}</span>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No teacher data yet</p>
                <p className="text-xs text-gray-400 mt-1">Top contributors will appear here</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Teacher & Class Progress */}
      {coverage?.teacher_progress?.length > 0 && (
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <User className="w-6 h-6 text-[#D67C2C]" />
            <h2 className="text-2xl font-bold text-gray-900">Teacher & Class Progress</h2>
          </div>
          <p className="text-gray-600 mb-6">Overview of each teacher's HPC contributions to help with decision-making</p>

          <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teacher</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Classes</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grades</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Students</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activities</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submissions</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Feedback</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reports</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Feedback Rate</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {coverage.teacher_progress.map((tp, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900">{tp.teacher_name}</div>
                        <div className="text-xs text-gray-500">{tp.email}</div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">{tp.class_count}</td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1">
                          {(tp.grades_covered || []).map((g, i) => (
                            <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                              {g}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        <span className="text-green-600 font-medium">{tp.students_with_reports}</span>
                        <span className="text-gray-400"> / </span>
                        <span>{tp.total_students}</span>
                      </td>
                      <td className="px-4 py-4 text-sm font-medium text-gray-900">{tp.activities_created}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">{tp.submissions_received}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">{tp.feedback_given}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">{tp.reports_generated}</td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-500 ${
                                tp.feedback_rate >= 80 ? 'bg-green-500' :
                                tp.feedback_rate >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.min(tp.feedback_rate, 100)}%` }}
                            ></div>
                          </div>
                          <span className={`text-xs font-bold ${
                            tp.feedback_rate >= 80 ? 'text-green-600' :
                            tp.feedback_rate >= 50 ? 'text-yellow-600' : 'text-red-600'
                          }`}>{tp.feedback_rate}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions — only shown for admin, not management (view-only role) */}
      {!isManagement && (
        <div className="mt-8">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-100">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D67C2C] to-orange-600 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Ready to Generate Reports?</h3>
                <p className="text-gray-600 mb-4">Navigate to the Classes or Students tab to select students and generate their Holistic Progress Card reports.</p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setActiveTab('classes')}
                    className="px-4 py-2 bg-white text-[#D67C2C] rounded-lg font-medium hover:bg-gray-50 transition-colors border border-orange-200 shadow-sm"
                  >
                    Browse by Classes
                  </button>
                  <button
                    onClick={() => setActiveTab('students')}
                    className="px-4 py-2 bg-white text-[#D67C2C] rounded-lg font-medium hover:bg-gray-50 transition-colors border border-orange-200 shadow-sm"
                  >
                    Browse by Students
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // ─── Classes Tab ──────────────────────────────────────────────────────────
  const renderClasses = () => {
    // If a student is selected, show activities
    if (selectedStudent) {
      return (
        <div>
          <button onClick={handleBackFromStudent} className="flex items-center gap-2 text-[#D67C2C] hover:underline mb-4 font-medium">
            <ChevronLeft className="w-4 h-4" /> Back to Students
          </button>
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <User className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{selectedStudent.name}</h3>
                <p className="text-sm text-gray-500">{selectedStudent.email} &middot; {selectedStudent.grade || 'N/A'}</p>
              </div>
            </div>
          </div>
          {renderActivitySelection()}
        </div>
      );
    }

    // If a class is selected, show sub-tabs: Students | Class Activities
    if (selectedClass) {
      // Derive filtered class activities for the activities sub-tab
      const filteredClassActivities = classActivities.filter(a => {
        if (classActivityStageFilter && a.stage !== classActivityStageFilter) return false;
        if (classActivityDomainFilter && !(a.domain || '').toLowerCase().includes(classActivityDomainFilter.toLowerCase())) return false;
        return true;
      });
      const allClassActsSelected = filteredClassActivities.length > 0 &&
        filteredClassActivities.every(a => classActivitySelected.includes(a.id));
      const classActsMixedStages = (() => {
        const selActs = classActivities.filter(a => classActivitySelected.includes(a.id));
        return [...new Set(selActs.map(a => a.stage).filter(Boolean))].length > 1;
      })();

      return (
        <div>
          <button onClick={handleBackFromClass} className="flex items-center gap-2 text-[#D67C2C] hover:underline mb-4 font-medium">
            <ChevronLeft className="w-4 h-4" /> Back to Classes
          </button>

          {/* Class header card */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedClass.name}</h3>
                  <p className="text-sm text-gray-500">Grade {selectedClass.grade} &middot; {renderStageTag(selectedClass.stage)} &middot; {classStudents.length} students</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sub-tabs */}
          <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
            <button
              onClick={() => setClassTab('students')}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${classTab === 'students' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <span className="flex items-center gap-2"><Users className="w-4 h-4" /> Students ({classStudents.length})</span>
            </button>
            <button
              onClick={() => {
                setClassTab('activities');
                if (classActivities.length === 0) handleLoadClassActivities();
              }}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${classTab === 'activities' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <span className="flex items-center gap-2"><BookOpen className="w-4 h-4" /> Class Activities</span>
            </button>
          </div>

          {/* Students sub-tab */}
          {classTab === 'students' && (
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
              <div className="p-6 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Students in {selectedClass.name}</h2>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activities</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {classStudents.map((student) => (
                      <tr key={student.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <User className="w-4 h-4 text-gray-400" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">{student.name}</div>
                              <div className="text-sm text-gray-500">{student.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{student.grade || 'N/A'}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{student.total_activities || 0}</td>
                        <td className="px-6 py-4">
                          {student.has_report ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3" /> Generated
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleStudentClick(student)}
                            className="inline-flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Eye className="w-4 h-4" /> View Activities
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {classStudents.length === 0 && (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium text-gray-500">No students found in this class</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Class Activities sub-tab */}
          {classTab === 'activities' && (
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
              {/* Header with filters + generate button */}
              <div className="p-6 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-indigo-600">
                      <BookOpen className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Class Activities</h2>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {filteredClassActivities.length} activities
                        {classActivitySelected.length > 0 && ` · ${classActivitySelected.length} selected`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* Stage filter */}
                    <select
                      value={classActivityStageFilter}
                      onChange={e => setClassActivityStageFilter(e.target.value)}
                      className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">All Stages</option>
                      <option value="Foundational Stage">Foundational</option>
                      <option value="Preparatory Stage">Preparatory</option>
                      <option value="Middle Stage">Middle</option>
                    </select>
                    <button
                      onClick={() => {
                        if (allClassActsSelected) {
                          setClassActivitySelected(prev => prev.filter(id => !filteredClassActivities.map(a => a.id).includes(id)));
                        } else {
                          setClassActivitySelected(prev => [...new Set([...prev, ...filteredClassActivities.map(a => a.id)])]);
                        }
                      }}
                      className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      {allClassActsSelected ? 'Deselect All' : 'Select All'}
                    </button>
                    {!isManagement && (
                      <button
                        onClick={handleBulkGenerateFromClassTab}
                        disabled={isBulkGenerating || classActivitySelected.length === 0 || classActsMixedStages}
                        title={classActsMixedStages ? 'Selected activities span multiple stages' : `Generate HPC reports for all ${classStudents.length} students`}
                        className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                      >
                        {isBulkGenerating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                        {isBulkGenerating ? 'Starting...' : `Generate for All Students (${classStudents.length})`}
                      </button>
                    )}
                  </div>
                </div>
                {classActsMixedStages && classActivitySelected.length > 0 && (
                  <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    Selected activities span multiple stages. Filter by a single stage to generate reports.
                  </div>
                )}
              </div>

              {/* Activity table */}
              {classActivitiesLoading ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-8 h-8 mx-auto mb-3 text-indigo-500 animate-spin" />
                  <p className="text-gray-500">Loading class activities...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                          <input
                            type="checkbox"
                            checked={allClassActsSelected && filteredClassActivities.length > 0}
                            onChange={() => {
                              if (allClassActsSelected) {
                                setClassActivitySelected(prev => prev.filter(id => !filteredClassActivities.map(a => a.id).includes(id)));
                              } else {
                                setClassActivitySelected(prev => [...new Set([...prev, ...filteredClassActivities.map(a => a.id)])]);
                              }
                            }}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-96">Activity</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-5 ">Domain</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-5 ">Stage</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-5 ">Submissions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredClassActivities.map(activity => (
                        <tr
                          key={activity.id}
                          className={`hover:bg-gray-50 transition-colors cursor-pointer ${classActivitySelected.includes(activity.id) ? 'bg-indigo-50' : ''}`}
                          onClick={() => setClassActivitySelected(prev =>
                            prev.includes(activity.id) ? prev.filter(id => id !== activity.id) : [...prev, activity.id]
                          )}
                        >
                          <td className="px-4 py-4" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={classActivitySelected.includes(activity.id)}
                              onChange={() => setClassActivitySelected(prev =>
                                prev.includes(activity.id) ? prev.filter(id => id !== activity.id) : [...prev, activity.id]
                              )}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                            />
                          </td>
                          <td className="px-6 py-4 max-w-xl w-96">
                            <div className="text-sm font-medium text-gray-900 prose prose-sm max-w-none">
                              <ReactMarkdown>{activity.activity || 'Untitled'}</ReactMarkdown>
                            </div>
                            {activity.teacher_name && (
                              <div className="text-xs text-gray-500 mt-0.5">{activity.teacher_name}</div>
                            )}
                            {activity.created_at && (
                              <div className="text-xs text-gray-400 mt-0.5">{displayDate(activity.created_at)}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">{activity.domain || 'N/A'}</td>
                          <td className="px-6 py-4">{renderStageTag(activity.stage)}</td>
                          <td className="px-6 py-4">
                            {activity.submission_count > 0 ? (
                              <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                                <CheckCircle className="w-3 h-3" /> {activity.submission_count}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">0</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredClassActivities.length === 0 && !classActivitiesLoading && (
                    <div className="text-center py-12">
                      <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg font-medium text-gray-500">No activities found for this class</p>
                      <p className="text-sm text-gray-400">Activities are created by the teacher via domain assignments</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    // Default: show class list
    return (
      <>
        {/* Filter Bar */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
          <div className="p-6 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                <Filter className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Filter Classes</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={classFilters.search}
                    onChange={(e) => setClassFilters(prev => ({ ...prev, search: e.target.value }))}
                    placeholder="Search by class name..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Stage</label>
                <select
                  value={classFilters.stage}
                  onChange={(e) => setClassFilters(prev => ({ ...prev, stage: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                >
                  <option value="">All Stages</option>
                  <option value="Foundational Stage">Foundational Stage</option>
                  <option value="Preparatory Stage">Preparatory Stage</option>
                  <option value="Middle Stage">Middle Stage</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Grade</label>
                <select
                  value={classFilters.grade}
                  onChange={(e) => setClassFilters(prev => ({ ...prev, grade: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                >
                  <option value="">All Grades</option>
                  {['BV1','BV2','BV3','1','2','3','4','5','6','7','8','9','10','11','12'].map(g => (
                    <option key={g} value={g}>Grade {g}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Teacher</label>
                <select
                  value={classFilters.teacher_id}
                  onChange={(e) => setClassFilters(prev => ({ ...prev, teacher_id: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                >
                  <option value="">All Teachers</option>
                  {allTeachersList.map(t => (
                    <option key={t.teacher_id} value={t.teacher_id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Actions</label>
                <button
                  onClick={() => setClassFilters({ stage: '', search: '', grade: '', teacher_id: '' })}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Classes Table */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
          <div className="p-6 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">All Classes</h2>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teacher</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Students</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activities</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {classes.map((cls) => (
                  <tr key={cls.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{cls.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{cls.grade}</td>
                    <td className="px-6 py-4">{renderStageTag(cls.stage)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{cls.teacher_name || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{cls.student_count || 0}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{cls.activity_count || 0}</td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleClassClick(cls)}
                        className="inline-flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {classes.length === 0 && (
              <div className="text-center py-12">
                <GraduationCap className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium text-gray-500">No classes found</p>
                <p className="text-sm text-gray-400">Try adjusting your filters</p>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  // ─── Activity Selection (shared by Classes & Students tabs) ───────────────
  const renderActivitySelection = () => {
    // If viewing a submission detail
    if (viewingAssignment && viewingSubmission) {
      return renderSubmissionDetail();
    }
    // If viewing submissions list for an assignment
    if (viewingAssignment && assignmentSubmissions.length > 0) {
      return renderSubmissionsList();
    }
    // If viewing assignment detail
    if (viewingAssignment) {
      return renderAssignmentDetail();
    }

    const visibleIds = filteredActivities.map(a => a.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedActivities.includes(id));
    // Determine the stage of currently selected activities for the Generate button label
    const selectedActs = activities.filter(a => selectedActivities.includes(a.id));
    const selectedStages = [...new Set(selectedActs.map(a => a.stage).filter(Boolean))];
    const mixedStages = selectedStages.length > 1;

    return (
    <div className="space-y-4">
      {/* Activity Filters */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
        <div className="p-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-3">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-700">Filter Activities</span>
          </div>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Stage</label>
              <select
                value={activityStageFilter}
                onChange={(e) => setActivityStageFilter(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
              >
                <option value="">All Stages</option>
                {activityStages.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Domain</label>
              <select
                value={activityDomainFilter}
                onChange={(e) => setActivityDomainFilter(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
              >
                <option value="">All Domains</option>
                {activityDomains.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Class</label>
              <select
                value={activityClassFilter}
                onChange={(e) => setActivityClassFilter(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
              >
                <option value="">All Classes</option>
                {activityClasses.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Teacher</label>
              <select
                value={activityTeacherFilter}
                onChange={(e) => setActivityTeacherFilter(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
              >
                <option value="">All Teachers</option>
                {activityTeachers.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">&nbsp;</label>
              <button
                onClick={() => { setActivityStageFilter(''); setActivityDomainFilter(''); setActivityClassFilter(''); setActivityTeacherFilter(''); }}
                className="w-full px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Activities Table */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
        <div className="p-6 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">
                  Activities for {selectedStudent?.name}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Showing {filteredActivities.length} of {activities.length} activities
                  {selectedActivities.length > 0 && ` · ${selectedActivities.length} selected`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSelectAllActivities}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {allVisibleSelected ? 'Deselect All' : 'Select All'}
              </button>
              {!isManagement && (
                <>
                  <button
                    onClick={handleGenerateReport}
                    disabled={generating || selectedActivities.length === 0 || mixedStages || !selectedStudent}
                    title={mixedStages ? 'Selected activities span multiple stages. Filter by one stage.' : !selectedStudent ? 'Select a student first' : ''}
                    className="px-4 py-2 text-sm bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                    {generating ? 'Generating...' : `Generate Report (${selectedActivities.length})`}
                  </button>
                  {selectedClass && (
                    <button
                      onClick={handleBulkGenerateReports}
                      disabled={isBulkGenerating || selectedActivities.length === 0 || mixedStages}
                      title={mixedStages ? 'Selected activities span multiple stages. Filter by one stage.' : `Generate reports for all ${classStudents.length} students in ${selectedClass.name}`}
                      className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                      {isBulkGenerating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                      {isBulkGenerating ? 'Starting...' : `Generate for Class (${classStudents.length})`}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          {mixedStages && selectedActivities.length > 0 && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              Selected activities span multiple stages ({selectedStages.join(', ')}). Filter by a single stage to generate a report.
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected && filteredActivities.length > 0}
                    onChange={handleSelectAllActivities}
                    className="rounded border-gray-300 text-[#D67C2C] focus:ring-[#D67C2C]"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-96">Activity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-5">Domain</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-5">Stage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-5">Teacher</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-5">Submission</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-5">Feedback</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-5">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredActivities.map((act) => (
                <tr key={act.id} className={`hover:bg-gray-50 transition-colors ${selectedActivities.includes(act.id) ? 'bg-orange-50' : ''}`}>
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedActivities.includes(act.id)}
                      onChange={() => handleActivityToggle(act.id)}
                      className="rounded border-gray-300 text-[#D67C2C] focus:ring-[#D67C2C]"
                    />
                  </td>
                  <td className="px-6 py-4 max-w-xl w-96">
                    <div className="text-sm font-medium text-gray-900 prose prose-sm max-w-none">
                      <ReactMarkdown>{act.activity || 'Untitled'}</ReactMarkdown>
                    </div>
                    {act.class_name && <div className="text-xs text-gray-500 mt-0.5">{act.class_name}</div>}
                    {act.is_published === false && (
                      <span className="mt-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800" title="Students and parents only see published activities, so a student report leaves this one out.">
                        Not published ({act.approval_status === 'approved' ? 'approved, waiting for the teacher to publish' : 'still in approval'})
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{act.domain || 'N/A'}</td>
                  <td className="px-6 py-4">{renderStageTag(act.stage)}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{act.teacher_name || 'N/A'}</td>
                  <td className="px-6 py-4">
                    {act.has_student_submission ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                        <CheckCircle className="w-3 h-3" /> Submitted
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">Not submitted</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {act.has_teacher_feedback ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                        <CheckCircle className="w-3 h-3" /> Done
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">Pending</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleViewAssignment(act.id)}
                      className="inline-flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Eye className="w-4 h-4" /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredActivities.length === 0 && (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium text-gray-500">
                {activities.length === 0 ? 'No activities found for this student' : 'No activities match the current filters'}
              </p>
              {activities.length > 0 && (
                <button
                  onClick={() => { setActivityStageFilter(''); setActivityDomainFilter(''); setActivityClassFilter(''); setActivityTeacherFilter(''); }}
                  className="mt-2 text-sm text-[#D67C2C] hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    );
  };

  // ─── Assignment Detail (drill-down from activity row) ───────────────────
  const renderAssignmentDetail = () => {
    if (!viewingAssignment) return null;
    
    return (
      <HPCAssignmentDetailView
        assignment={viewingAssignment}
        onBack={handleBackFromAssignment}
        onViewSubmissions={handleViewSubmissions}
        submissionCount={viewingAssignment.submission_count || 0}
      />
    );
  };

  // ─── Submissions List (drill-down from assignment detail) ─────────────────
  const renderSubmissionsList = () => {
    const a = viewingAssignment;
    if (!a) return null;
    const totalSubs = assignmentSubmissions.length;
    const withFeedback = assignmentSubmissions.filter(s => s.has_teacher_feedback).length;

    return (
      <div className="space-y-4">
        <button onClick={handleBackFromSubmissions} className="flex items-center gap-2 text-[#D67C2C] hover:underline font-medium">
          <ChevronLeft className="w-4 h-4" /> Back to Assignment Detail
        </button>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500">Total Submissions</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">{totalSubs}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500">With Feedback</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{withFeedback}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500">Pending Feedback</p>
            <p className="text-2xl font-bold text-orange-600 mt-1">{totalSubs - withFeedback}</p>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
          <div className="p-6 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Submissions</h2>
                <p className="text-xs text-gray-500 mt-0.5">{a.activity?.substring(0, 80)}{a.activity?.length > 80 ? '...' : ''}</p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Feedback</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {assignmentSubmissions.map((sub) => (
                  <tr key={sub.submission_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <User className="w-4 h-4 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{sub.student?.name || 'Unknown'}</div>
                          <div className="text-xs text-gray-500">{sub.student?.email || ''}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{sub.submitted_at ? displayDate(sub.submitted_at) : '—'}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        sub.status === 'submitted' || sub.status === 'graded'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {sub.status || 'pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {sub.has_teacher_feedback ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                          <CheckCircle className="w-3 h-3" /> Done
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Pending</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleViewSubmissionDetail(sub)}
                        className="inline-flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {assignmentSubmissions.length === 0 && (
              <div className="text-center py-12">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium text-gray-500">No submissions yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── Submission Detail (drill-down from submissions list) ─────────────────
  const renderSubmissionDetail = () => {
    const sub = viewingSubmission;
    const a = viewingAssignment;
    if (!sub || !a) return null;
    const stage = a.stage || '';

    // Helper to render self-assessment
    const renderSelfAssessment = (data) => {
      if (!data) return null;
      if (typeof data === 'string') return <p className="text-sm text-gray-800 whitespace-pre-wrap">{data}</p>;
      
      // Handle structured self-assessment data
      return (
        <div className="space-y-2">
          {data.liked !== undefined && (
            <div><span className="font-medium">Liked:</span> {data.liked ? 'Yes' : 'No'}</div>
          )}
          {data.easy !== undefined && (
            <div><span className="font-medium">Easy:</span> {data.easy ? 'Yes' : 'No'}</div>
          )}
          {data.needed && data.needed.length > 0 && (
            <div><span className="font-medium">Needed help from:</span> {data.needed.join(', ')}</div>
          )}
        </div>
      );
    };

    // Helper to render peer-assessment
    const renderPeerAssessment = (data) => {
      if (!data) return null;
      if (typeof data === 'string') return <p className="text-sm text-gray-800 whitespace-pre-wrap">{data}</p>;
      
      // Handle structured peer-assessment data
      return (
        <div className="space-y-2">
          {data.friend_liked !== undefined && (
            <div><span className="font-medium">Friend liked:</span> {data.friend_liked ? 'Yes' : 'No'}</div>
          )}
          {data.friend_easy !== undefined && (
            <div><span className="font-medium">Friend found it easy:</span> {data.friend_easy ? 'Yes' : 'No'}</div>
          )}
          {data.friend_needed && data.friend_needed.length > 0 && (
            <div><span className="font-medium">Friend needed help from:</span> {data.friend_needed.join(', ')}</div>
          )}
        </div>
      );
    };

    // Helper to render caregiver observation
    const renderCaregiverObservation = (data) => {
      if (!data) return null;
      if (typeof data === 'string') return <p className="text-sm text-gray-800 whitespace-pre-wrap">{data}</p>;
      
      // Handle structured caregiver observation data
      return (
        <div className="space-y-2">
          {data.resources && data.resources.length > 0 && (
            <div><span className="font-medium">Resources used:</span> {data.resources.join(', ')}</div>
          )}
          {data.comments && (
            <div><span className="font-medium">Comments:</span> {data.comments}</div>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-4">
        <button onClick={handleBackFromSubmissionDetail} className="flex items-center gap-2 text-[#D67C2C] hover:underline font-medium">
          <ChevronLeft className="w-4 h-4" /> Back to Submissions
        </button>

        {/* Student Info */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
              <User className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{sub.student?.name || 'Unknown'}</h3>
              <p className="text-sm text-gray-500">
                {sub.student?.email || ''} · Grade {sub.student?.grade || 'N/A'} · {renderStageTag(stage)}
              </p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-xs text-gray-500">Submitted</p>
              <p className="text-sm font-medium text-gray-700">{sub.submitted_at ? displayDate(sub.submitted_at) : '—'}</p>
            </div>
          </div>
        </div>

        {/* Answers */}
        {sub.answers?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#D67C2C]" /> Student Answers
              </h3>
            </div>
            <div className="p-4 space-y-4">
              {sub.answers.map((ans, i) => {
                const question = a.assessment_questions?.[i];
                const qText = question ? (typeof question === 'string' ? question : question.question || question.text || `Question ${i + 1}`) : `Question ${i + 1}`;
                const aText = typeof ans === 'string' ? ans : ans.answer || ans.text || JSON.stringify(ans);
                return (
                  <div key={i} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                    <p className="text-xs font-medium text-gray-500 mb-1">Q{i + 1}: {qText}</p>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{aText}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* File Submission */}
        {sub.file_path && (
          <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <Download className="w-4 h-4 text-[#D67C2C]" /> File Submission
            </h3>
            <a
              href={sub.file_path.startsWith('http') ? sub.file_path : `${API_BASE_URL}${sub.file_path}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <Download className="w-4 h-4" /> Download File
            </a>
          </div>
        )}

        {/* Teacher Feedback / Observational Notes */}
        {(sub.teacher_feedback || sub.observational_notes) && (
          <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100 bg-green-50">
              <h3 className="text-sm font-semibold text-green-800 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> Teacher Feedback
              </h3>
            </div>
            <div className="p-4 space-y-3">
              {sub.observational_notes && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Observational Notes</label>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">{sub.observational_notes}</p>
                </div>
              )}
              {sub.teacher_feedback && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Feedback</label>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                    {typeof sub.teacher_feedback === 'string' ? sub.teacher_feedback : JSON.stringify(sub.teacher_feedback, null, 2)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Proficiency Marks */}
        {sub.proficiency_marks?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100 bg-purple-50">
              <h3 className="text-sm font-semibold text-purple-800 flex items-center gap-2">
                <BarChart3 className="w-4 h-4" /> Proficiency Marks
              </h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {sub.proficiency_marks.map((mark, i) => {
                  const levelColors = {
                    'stream': 'bg-blue-100 text-blue-800 border-blue-200',
                    'mountain': 'bg-green-100 text-green-800 border-green-200',
                    'sky': 'bg-purple-100 text-purple-800 border-purple-200',
                    'beginner': 'bg-yellow-100 text-yellow-800 border-yellow-200',
                    'proficient': 'bg-green-100 text-green-800 border-green-200',
                    'advanced': 'bg-purple-100 text-purple-800 border-purple-200',
                  };
                  const level = (mark.level || mark.proficiency || '').toLowerCase();
                  const colorClass = levelColors[level] || 'bg-gray-100 text-gray-800 border-gray-200';
                  return (
                    <div key={i} className={`p-3 rounded-lg border ${colorClass}`}>
                      <p className="text-xs font-medium truncate">{mark.ability || mark.competency || mark.name || `Item ${i + 1}`}</p>
                      <p className="text-sm font-semibold mt-1 capitalize">{mark.level || mark.proficiency || '—'}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Self Assessment */}
        {sub.self_assessment && (
          <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100 bg-blue-50">
              <h3 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                <User className="w-4 h-4" /> Self Assessment
              </h3>
            </div>
            <div className="p-4">
              {renderSelfAssessment(sub.self_assessment)}
            </div>
          </div>
        )}

        {/* Peer Assessment */}
        {sub.peer_assessment && (
          <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100 bg-indigo-50">
              <h3 className="text-sm font-semibold text-indigo-800 flex items-center gap-2">
                <Users className="w-4 h-4" /> Peer Assessment
              </h3>
            </div>
            <div className="p-4">
              {renderPeerAssessment(sub.peer_assessment)}
            </div>
          </div>
        )}

        {/* Caregiver Observation */}
        {sub.caregiver_observation && (
          <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100 bg-pink-50">
              <h3 className="text-sm font-semibold text-pink-800 flex items-center gap-2">
                <Eye className="w-4 h-4" /> Caregiver Observation
              </h3>
            </div>
            <div className="p-4">
              {renderCaregiverObservation(sub.caregiver_observation)}
            </div>
          </div>
        )}

        {/* Learner Reflection (Preparatory / Middle) */}
        {sub.learner_reflection && (
          <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100 bg-teal-50">
              <h3 className="text-sm font-semibold text-teal-800 flex items-center gap-2">
                <BookOpen className="w-4 h-4" /> Learner Reflection
              </h3>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-800 whitespace-pre-wrap">
                {typeof sub.learner_reflection === 'string' ? sub.learner_reflection : JSON.stringify(sub.learner_reflection, null, 2)}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Students Tab ─────────────────────────────────────────────────────────
  const renderStudents = () => {
    // If a student is selected, show activities
    if (selectedStudent) {
      return (
        <div>
          <button onClick={handleBackFromStudent} className="flex items-center gap-2 text-[#D67C2C] hover:underline mb-4 font-medium">
            <ChevronLeft className="w-4 h-4" /> Back to Students
          </button>
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <User className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{selectedStudent.name}</h3>
                <p className="text-sm text-gray-500">{selectedStudent.email} &middot; {selectedStudent.grade || 'N/A'} &middot; {renderStageTag(selectedStudent.stage)}</p>
              </div>
            </div>
          </div>
          {renderActivitySelection()}
        </div>
      );
    }

    const filteredStudents = students.filter(s => {
      if (studentGradeFilter && String(s.grade) !== studentGradeFilter) return false;
      return true;
    });

    return (
      <>
        {/* Filter Bar */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
          <div className="p-6 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                <Filter className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Filter Students</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={studentSearch}
                    onChange={(e) => { setStudentSearch(e.target.value); setStudentPage(1); }}
                    placeholder="Search by name or email..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Stage</label>
                <select
                  value={studentStageFilter}
                  onChange={(e) => { setStudentStageFilter(e.target.value); setStudentPage(1); }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                >
                  <option value="">All Stages</option>
                  <option value="Foundational Stage">Foundational Stage</option>
                  <option value="Preparatory Stage">Preparatory Stage</option>
                  <option value="Middle Stage">Middle Stage</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Grade</label>
                <select
                  value={studentGradeFilter}
                  onChange={(e) => setStudentGradeFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                >
                  <option value="">All Grades</option>
                  {['BV1','BV2','BV3','1','2','3','4','5','6','7','8','9','10','11','12'].map(g => (
                    <option key={g} value={g}>Grade {g}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Report Status</label>
                <select
                  value={studentReportFilter}
                  onChange={(e) => { setStudentReportFilter(e.target.value); setStudentPage(1); }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                >
                  <option value="">All</option>
                  <option value="true">Has Report</option>
                  <option value="false">No Report</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Actions</label>
                <button
                  onClick={() => { setStudentSearch(''); setStudentStageFilter(''); setStudentReportFilter(''); setStudentGradeFilter(''); setStudentPage(1); }}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Students Table */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
          <div className="p-6 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">All Students</h2>
              </div>
              <div className="text-sm text-gray-600">
                {totalStudents} total students
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activities</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submissions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredStudents.map((student) => (
                  <tr key={student.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <User className="w-4 h-4 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{student.name}</div>
                          <div className="text-sm text-gray-500">{student.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{student.grade || 'N/A'}</td>
                    <td className="px-6 py-4">{renderStageTag(student.stage)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{student.total_activities || 0}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{student.total_submissions || 0}</td>
                    <td className="px-6 py-4">
                      {student.has_report ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3" /> Generated
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleStudentClick(student)}
                        className="inline-flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" /> View Activities
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredStudents.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium text-gray-500">No students found</p>
                <p className="text-sm text-gray-400">Try adjusting your filters</p>
              </div>
            )}
          </div>
          {renderPagination(studentPage, setStudentPage, totalStudents)}
        </div>
      </>
    );
  };

  // ─── Reports Tab ──────────────────────────────────────────────────────────
  const renderReports = () => {
    const reportGeneratedByOptions = [...new Set(reports.map(r => r.generated_by_name).filter(Boolean))].sort();
    const filteredReports = reports.filter(r => {
      if (reportGradeFilter && String(r.student_grade) !== reportGradeFilter) return false;
      if (reportGeneratedByFilter && r.generated_by_name !== reportGeneratedByFilter) return false;
      if (reportStartDate && (!r.generated_at || new Date(r.generated_at) < new Date(reportStartDate))) return false;
      if (reportEndDate) {
        const end = new Date(reportEndDate);
        end.setHours(23, 59, 59, 999);
        if (!r.generated_at || new Date(r.generated_at) > end) return false;
      }
      return true;
    });

    return (
    <>
      {/* Filter Bar */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
              <Filter className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Filter Reports</h2>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={reportSearch}
                  onChange={(e) => { setReportSearch(e.target.value); setReportPage(1); }}
                  placeholder="Search by student name..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Stage</label>
              <select
                value={reportStageFilter}
                onChange={(e) => { setReportStageFilter(e.target.value); setReportPage(1); }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
              >
                <option value="">All Stages</option>
                <option value="Foundational Stage">Foundational Stage</option>
                <option value="Preparatory Stage">Preparatory Stage</option>
                <option value="Middle Stage">Middle Stage</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Grade</label>
              <select
                value={reportGradeFilter}
                onChange={(e) => setReportGradeFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
              >
                <option value="">All Grades</option>
                {['BV1','BV2','BV3','1','2','3','4','5','6','7','8','9','10','11','12'].map(g => (
                  <option key={g} value={g}>Grade {g}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-gray-100">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Generated By</label>
              <select
                value={reportGeneratedByFilter}
                onChange={(e) => setReportGeneratedByFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
              >
                <option value="">All</option>
                {reportGeneratedByOptions.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date From</label>
              <input
                type="date"
                value={reportStartDate}
                onChange={(e) => setReportStartDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date To</label>
              <input
                type="date"
                value={reportEndDate}
                onChange={(e) => setReportEndDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Actions</label>
              <button
                onClick={() => {
                  setReportSearch(''); setReportStageFilter(''); setReportGradeFilter('');
                  setReportGeneratedByFilter(''); setReportStartDate(''); setReportEndDate('');
                  setReportPage(1);
                }}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
        <div className="p-6 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Generated Reports</h2>
            </div>
            <div className="text-sm text-gray-600">
              {totalReports} total reports
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activities</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Generated By</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredReports.map((report) => (
                <tr key={report.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-gray-400" />
                      <div className="text-sm font-medium text-gray-900">{report.student_name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{report.student_grade || 'N/A'}</td>
                  <td className="px-6 py-4">{renderStageTag(report.stage)}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{report.selected_activity_count || 0}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{report.generated_by_name || 'Admin'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{displayDate(report.generated_at)}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewReport(report.id)}
                        className="inline-flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" /> View
                      </button>
                      {!isManagement && (
                        <button
                          onClick={() => handleDeleteReport(report.id)}
                          className="inline-flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" /> Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredReports.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium text-gray-500">No reports generated yet</p>
              <p className="text-sm text-gray-400">Generate reports from the Classes or Students tab</p>
            </div>
          )}
        </div>
        {renderPagination(reportPage, setReportPage, totalReports)}
      </div>
    </>
    );
  };

  // ─── Branding Tab ─────────────────────────────────────────────────────────
  const renderBranding = () => {
    // Management can only edit branding for their own main branch, not for other schools
    const brandingReadOnly = isManagement && selectedSchool?.school_id !== userSchoolId;
    return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Branding Form */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
        <div className="p-6 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">School Branding</h2>
            {brandingReadOnly && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                View Only
              </span>
            )}
          </div>
        </div>
        <form onSubmit={brandingReadOnly ? (e) => e.preventDefault() : handleBrandingSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">School Name</label>
            <input
              type="text"
              value={brandingForm.school_name}
              onChange={(e) => !brandingReadOnly && setBrandingForm(prev => ({ ...prev, school_name: e.target.value }))}
              readOnly={brandingReadOnly}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${brandingReadOnly ? 'bg-gray-50 cursor-default' : 'focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent'}`}
              placeholder="Enter school name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">School Address</label>
            <input
              type="text"
              value={brandingForm.school_address}
              onChange={(e) => !brandingReadOnly && setBrandingForm(prev => ({ ...prev, school_address: e.target.value }))}
              readOnly={brandingReadOnly}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${brandingReadOnly ? 'bg-gray-50 cursor-default' : 'focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent'}`}
              placeholder="Enter school address"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Principal Name</label>
            <input
              type="text"
              value={brandingForm.principal_name}
              onChange={(e) => !brandingReadOnly && setBrandingForm(prev => ({ ...prev, principal_name: e.target.value }))}
              readOnly={brandingReadOnly}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${brandingReadOnly ? 'bg-gray-50 cursor-default' : 'focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent'}`}
              placeholder="Enter principal name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Principal Designation</label>
            <input
              type="text"
              value={brandingForm.principal_designation}
              onChange={(e) => !brandingReadOnly && setBrandingForm(prev => ({ ...prev, principal_designation: e.target.value }))}
              readOnly={brandingReadOnly}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${brandingReadOnly ? 'bg-gray-50 cursor-default' : 'focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent'}`}
              placeholder="e.g. Principal, Head of School"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Academic Year</label>
            <input
              type="text"
              value={brandingForm.academic_year}
              onChange={(e) => !brandingReadOnly && setBrandingForm(prev => ({ ...prev, academic_year: e.target.value }))}
              readOnly={brandingReadOnly}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${brandingReadOnly ? 'bg-gray-50 cursor-default' : 'focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent'}`}
              placeholder="e.g. 2025-2026"
            />
          </div>
          {!brandingReadOnly && (
            <button
              type="submit"
              className="w-full px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors font-medium"
            >
              Save Branding
            </button>
          )}
        </form>
      </div>

      {/* File Uploads & Preview */}
      <div className="space-y-6">
        {/* Logo Upload */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
          <div className="p-6 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-100">
                <Image className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">School Logo</h2>
            </div>
          </div>
          <div className="p-6">
            {branding?.school_logo_url && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg flex items-center justify-center">
                <img
                  src={branding.school_logo_url.startsWith('http') ? branding.school_logo_url : `${API_BASE_URL}${branding.school_logo_url}`}
                  alt="School Logo"
                  className="max-h-32 object-contain"
                />
              </div>
            )}
            {!brandingReadOnly && (
              <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#D67C2C] hover:bg-orange-50 transition-colors">
                <Upload className="w-5 h-5 text-gray-500" />
                <span className="text-sm text-gray-600">Click to upload school logo</span>
                <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
              </label>
            )}
            {brandingReadOnly && !branding?.school_logo_url && (
              <p className="text-sm text-gray-400 text-center py-4">No logo uploaded</p>
            )}
          </div>
        </div>

        {/* Signature Upload */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
          <div className="p-6 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-100">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Principal Signature</h2>
            </div>
          </div>
          <div className="p-6">
            {branding?.principal_signature_url && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg flex items-center justify-center">
                <img
                  src={branding.principal_signature_url.startsWith('http') ? branding.principal_signature_url : `${API_BASE_URL}${branding.principal_signature_url}`}
                  alt="Principal Signature"
                  className="max-h-24 object-contain"
                />
              </div>
            )}
            {!brandingReadOnly && (
              <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#D67C2C] hover:bg-orange-50 transition-colors">
                <Upload className="w-5 h-5 text-gray-500" />
                <span className="text-sm text-gray-600">Click to upload principal signature</span>
                <input type="file" accept="image/*" onChange={handleSignatureUpload} className="hidden" />
              </label>
            )}
            {brandingReadOnly && !branding?.principal_signature_url && (
              <p className="text-sm text-gray-400 text-center py-4">No signature uploaded</p>
            )}
          </div>
        </div>

        {/* Branding Preview */}
        {branding && (
          <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-100">
                  <Eye className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Current Branding Preview</h2>
              </div>
            </div>
            <div className="p-6 space-y-2">
              <div className="flex justify-between"><span className="text-sm text-gray-500">School Name:</span><span className="text-sm font-medium text-gray-900">{branding.school_name || '—'}</span></div>
              <div className="flex justify-between"><span className="text-sm text-gray-500">Address:</span><span className="text-sm font-medium text-gray-900">{branding.school_address || '—'}</span></div>
              <div className="flex justify-between"><span className="text-sm text-gray-500">Principal:</span><span className="text-sm font-medium text-gray-900">{branding.principal_name || '—'}</span></div>
              <div className="flex justify-between"><span className="text-sm text-gray-500">Designation:</span><span className="text-sm font-medium text-gray-900">{branding.principal_designation || '—'}</span></div>
              <div className="flex justify-between"><span className="text-sm text-gray-500">Academic Year:</span><span className="text-sm font-medium text-gray-900">{branding.academic_year || '—'}</span></div>
            </div>
          </div>
        )}
      </div>
    </div>
    );
  };

  // ─── Teachers Tab ─────────────────────────────────────────────────────────
  const renderTeachers = () => {
    // If a teacher is selected, show their detail
    if (selectedTeacher && teacherDetail) {
      return (
        <div>
          <button 
            onClick={() => {
              setSelectedTeacher(null);
              setTeacherDetail(null);
            }} 
            className="flex items-center gap-2 text-[#D67C2C] hover:underline mb-6 font-medium"
          >
            <ChevronLeft className="w-4 h-4" /> Back to Teachers
          </button>

          {/* Teacher Header */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6 border border-purple-100 mb-6">
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center shadow-lg">
                  <User className="w-10 h-10 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{teacherDetail.name}</h2>
                <p className="text-gray-600 mb-4">{teacherDetail.email}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Classes</p>
                    <p className="text-2xl font-bold text-purple-600">{teacherDetail.classes?.length || 0}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Activities</p>
                    <p className="text-2xl font-bold text-blue-600">{teacherDetail.recent_activities?.length || 0}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Reports</p>
                    <p className="text-2xl font-bold text-green-600">{teacherDetail.recent_reports?.length || 0}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Students</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {teacherDetail.classes?.reduce((sum, cls) => sum + cls.student_count, 0) || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Classes Section */}
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-[#D67C2C]" />
              Classes Taught
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teacherDetail.classes && teacherDetail.classes.length > 0 ? (
                teacherDetail.classes.map((cls, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-md border border-gray-200 p-5 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">{cls.name}</h4>
                        {renderStageTag(cls.stage)}
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Students:</span>
                        <span className="font-semibold text-gray-900">{cls.student_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Activities:</span>
                        <span className="font-semibold text-gray-900">{cls.activity_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">With Reports:</span>
                        <span className="font-semibold text-green-600">{cls.students_with_reports}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-8 text-gray-500">
                  <GraduationCap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p>No classes assigned</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Activities & Reports */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Activities */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-blue-600" />
                Recent Activities
              </h3>
              <div className="space-y-3">
                {teacherDetail.recent_activities && teacherDetail.recent_activities.length > 0 ? (
                  teacherDetail.recent_activities.slice(0, 5).map((activity, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{activity.title || activity.activity || 'Untitled'}</p>
                        <p className="text-xs text-gray-500">{activity.domain || 'No domain'}</p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleViewAssignment(activity._id || activity.id); }}
                        className="inline-flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                      >
                        <Eye className="w-4 h-4" /> View
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No recent activities</p>
                )}
              </div>
            </div>

            {/* Recent Reports */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-600" />
                Recent Reports Generated
              </h3>
              <div className="space-y-3">
                {teacherDetail.recent_reports && teacherDetail.recent_reports.length > 0 ? (
                  teacherDetail.recent_reports.slice(0, 5).map((report, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{report.student_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {renderStageTag(report.stage)}
                          <span className="text-xs text-gray-500">
                            {new Date(report.generated_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No reports generated yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Default: show teacher list
    return (
      <>
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <User className="w-6 h-6 text-[#D67C2C]" />
            <h2 className="text-2xl font-bold text-gray-900">Teacher Overview</h2>
          </div>
          <p className="text-gray-600">View teacher-wise HPC activities, reports, and progress</p>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search teachers by name or email..."
                value={teacherSearch}
                onChange={(e) => setTeacherSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
              />
            </div>
            <button
              onClick={() => {
                setTeacherSearch('');
                setTeacherPage(1);
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium"
            >
              Clear
            </button>
          </div>
        </div>

        {/* Teachers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          {teachers.length > 0 ? (
            teachers.map((teacher, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl shadow-md border border-gray-200 p-6 hover:shadow-xl transition-all duration-300 cursor-pointer"
                onClick={() => {
                  setSelectedTeacher(teacher);
                  loadTeacherDetail(teacher.teacher_id);
                }}
              >
                {/* Teacher Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center flex-shrink-0">
                    <User className="w-7 h-7 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 truncate mb-1">{teacher.name}</h3>
                    <p className="text-xs text-gray-500 truncate">{teacher.email}</p>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs text-blue-600 font-medium mb-1">Activities</p>
                    <p className="text-2xl font-bold text-blue-700">{teacher.activity_count}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs text-green-600 font-medium mb-1">Reports</p>
                    <p className="text-2xl font-bold text-green-700">{teacher.report_count}</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-xs text-purple-600 font-medium mb-1">Classes</p>
                    <p className="text-2xl font-bold text-purple-700">{teacher.class_count}</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3">
                    <p className="text-xs text-orange-600 font-medium mb-1">Students</p>
                    <p className="text-2xl font-bold text-orange-700">{teacher.total_students}</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Report Coverage</span>
                    <span className="font-semibold text-gray-900">
                      {teacher.total_students > 0 
                        ? Math.round((teacher.students_with_reports / teacher.total_students) * 100)
                        : 0}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all duration-500"
                      style={{ 
                        width: `${teacher.total_students > 0 
                          ? (teacher.students_with_reports / teacher.total_students) * 100 
                          : 0}%` 
                      }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500">
                    {teacher.students_with_reports} of {teacher.total_students} students have reports
                  </p>
                </div>

                {/* View Details Button */}
                <button className="w-full mt-4 px-4 py-2 bg-gradient-to-r from-[#D67C2C] to-orange-600 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2">
                  <Eye className="w-4 h-4" />
                  View Details
                </button>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No teachers found</p>
              <p className="text-gray-400 text-sm mt-2">Try adjusting your search criteria</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {renderPagination(teacherPage, setTeacherPage, totalTeachers)}
      </>
    );
  };

  // ─── Coverage Tab ─────────────────────────────────────────────────────────
  const renderCoverage = () => {
    const coverageTeacherOptions = [...new Set((coverage?.teacher_progress || []).map(tp => tp.teacher_name).filter(Boolean))].sort();
    const filteredTeacherProgress = (coverage?.teacher_progress || []).filter(tp => {
      if (coverageTeacherFilter && tp.teacher_name !== coverageTeacherFilter) return false;
      return true;
    });

    return (
    <>
      {/* Coverage Stats */}
      {coverage && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Students</p>
                <p className="text-3xl font-bold text-blue-600 mt-1">{coverage.total_students || 0}</p>
              </div>
              <div className="p-3 rounded-full bg-blue-100">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">With Reports</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{coverage.students_with_reports || 0}</p>
              </div>
              <div className="p-3 rounded-full bg-green-100">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Without Reports</p>
                <p className="text-3xl font-bold text-red-600 mt-1">{coverage.students_without_reports || 0}</p>
              </div>
              <div className="p-3 rounded-full bg-red-100">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Coverage Rate</p>
                <p className="text-3xl font-bold text-purple-600 mt-1">
                  {coverage.total_students > 0
                    ? Math.round((coverage.students_with_reports / coverage.total_students) * 100)
                    : 0}%
                </p>
              </div>
              <div className="p-3 rounded-full bg-purple-100">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Teacher Progress Details */}
      {coverage?.teacher_progress?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
          <div className="p-6 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Teacher Progress</h2>
                  <p className="text-sm text-gray-500 mt-1">Detailed breakdown of each teacher's HPC contributions</p>
                </div>
              </div>
              <button
                onClick={() => {
                  const rows = filteredTeacherProgress.map(tp => ({
                    'Teacher Name': tp.teacher_name,
                    'Email': tp.email,
                    'Classes': tp.class_count,
                    'Grades': (tp.grades_covered || []).join(', '),
                    'Total Students': tp.total_students,
                    'Activities Created': tp.activities_created,
                    'Submissions Received': tp.submissions_received,
                    'Feedback Given': tp.feedback_given,
                    'Self Assessments': tp.self_assessments,
                    'Peer Assessments': tp.peer_assessments,
                    'Caregiver Observations': tp.caregiver_observations,
                    'Reports Generated': tp.reports_generated,
                    'Feedback Rate (%)': tp.feedback_rate,
                  }));
                  const headers = Object.keys(rows[0]);
                  const csvContent = [
                    headers.join(','),
                    ...rows.map(row => headers.map(h => {
                      const val = String(row[h] ?? '');
                      return val.includes(',') || val.includes('"') ? `"${val.replace(/"/g, '""')}"` : val;
                    }).join(','))
                  ].join('\n');
                  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `Teacher_Progress_${new Date().toISOString().slice(0, 10)}.csv`;
                  link.click();
                  URL.revokeObjectURL(url);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200 text-sm"
              >
                <Download className="w-4 h-4" />
                Export to Excel
              </button>
            </div>
          </div>
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
            <div className="flex items-end gap-4">
              <div className="w-64">
                <label className="block text-xs font-medium text-gray-600 mb-1">Teacher</label>
                <select
                  value={coverageTeacherFilter}
                  onChange={(e) => setCoverageTeacherFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                >
                  <option value="">All Teachers</option>
                  {coverageTeacherOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              {coverageTeacherFilter && (
                <button
                  onClick={() => setCoverageTeacherFilter('')}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Clear Filter
                </button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teacher</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Classes</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grades</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Students</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activities</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submissions</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Feedback</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Self</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Peer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Caregiver</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reports</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Feedback Rate</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTeacherProgress.map((tp, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900">{tp.teacher_name}</div>
                      <div className="text-xs text-gray-500">{tp.email}</div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">{tp.class_count}</td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(tp.grades_covered || []).map((g, i) => (
                          <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            {g}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{tp.total_students}</td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{tp.activities_created}</td>
                    <td className="px-4 py-4 text-sm text-gray-600">{tp.submissions_received}</td>
                    <td className="px-4 py-4 text-sm text-gray-600">{tp.feedback_given}</td>
                    <td className="px-4 py-4 text-sm text-gray-600">{tp.self_assessments}</td>
                    <td className="px-4 py-4 text-sm text-gray-600">{tp.peer_assessments}</td>
                    <td className="px-4 py-4 text-sm text-gray-600">{tp.caregiver_observations}</td>
                    <td className="px-4 py-4 text-sm text-gray-600">{tp.reports_generated}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-500 ${
                              tp.feedback_rate >= 80 ? 'bg-green-500' :
                              tp.feedback_rate >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(tp.feedback_rate, 100)}%` }}
                          ></div>
                        </div>
                        <span className={`text-xs font-bold ${
                          tp.feedback_rate >= 80 ? 'text-green-600' :
                          tp.feedback_rate >= 50 ? 'text-yellow-600' : 'text-red-600'
                        }`}>{tp.feedback_rate}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Uncovered Students */}
      {coverage?.uncovered_students?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
          <div className="p-6 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-100">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Students Without Reports</h2>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activities</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {coverage.uncovered_students.map((student, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{student.name}</div>
                      <div className="text-sm text-gray-500">{student.email}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{student.grade || 'N/A'}</td>
                    <td className="px-6 py-4">{renderStageTag(student.stage)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{student.total_activities || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Activities Missing Feedback */}
      {coverage?.activities_missing_feedback?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
          <div className="p-6 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-yellow-100">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Activities Missing Teacher Feedback</h2>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-96">Activity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teacher</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Missing Feedback</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {coverage.activities_missing_feedback.map((act, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 max-w-md truncate">{act.activity || 'Untitled'}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{act.domain || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{act.teacher_name || 'N/A'}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1 text-xs text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                        <AlertCircle className="w-3 h-3" /> {act.submissions_without_feedback || 0} submissions
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleViewAssignment(act.assignment_id)}
                        className="inline-flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!coverage && (
        <div className="text-center py-12">
          <ClipboardList className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium text-gray-500">Loading coverage data...</p>
        </div>
      )}
    </>
    );
  };

  // ─── Management: Schools Overview ────────────────────────────────────────
  const renderManagementSchools = () => {
    if (managementSchoolsLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 text-[#D67C2C] animate-spin" />
          <span className="ml-3 text-gray-600 font-medium">Loading schools...</span>
        </div>
      );
    }
    return (
      <div>
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <GraduationCap className="w-6 h-6 text-[#D67C2C]" />
            <h2 className="text-2xl font-bold text-gray-900">Schools Overview</h2>
          </div>
          <p className="text-gray-600">Select a school to view its HPC progress in detail.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {managementSchools.map((school) => (
            <div
              key={school.school_id}
              onClick={() => setSelectedSchool(school)}
              className="bg-white rounded-2xl shadow-md border border-gray-200 p-6 hover:shadow-xl hover:border-orange-300 transition-all duration-300 cursor-pointer"
            >
              <div className="flex items-start gap-4 mb-5">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="w-7 h-7 text-[#D67C2C]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 truncate mb-1">{school.school_name}</h3>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    school.school_type === 'group_parent'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {school.school_type === 'group_parent' ? 'Main Branch' : 'Branch'}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-blue-600 font-medium mb-1">Activities</p>
                  <p className="text-xl font-bold text-blue-700">{school.total_activities || 0}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-green-600 font-medium mb-1">Reports</p>
                  <p className="text-xl font-bold text-green-700">{school.reports_generated || 0}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-xs text-purple-600 font-medium mb-1">Students</p>
                  <p className="text-xl font-bold text-purple-700">{school.total_students || 0}</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-3">
                  <p className="text-xs text-orange-600 font-medium mb-1">Pending</p>
                  <p className="text-xl font-bold text-orange-700">{school.students_without_reports || 0}</p>
                </div>
              </div>
              {(school.total_students || 0) > 0 && (
                <div className="space-y-1 mb-4">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Report Coverage</span>
                    <span className="font-semibold text-gray-900">
                      {Math.round(((school.reports_generated || 0) / school.total_students) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(Math.round(((school.reports_generated || 0) / school.total_students) * 100), 100)}%` }}
                    ></div>
                  </div>
                </div>
              )}
              <button className="w-full px-4 py-2 bg-gradient-to-r from-[#D67C2C] to-orange-600 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2">
                <Eye className="w-4 h-4" />
                View Details
              </button>
            </div>
          ))}
          {managementSchools.length === 0 && (
            <div className="col-span-full text-center py-16">
              <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-500">No schools found in your group</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── Main Render ──────────────────────────────────────────────────────────
  return (
    <>
      <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

          {/* Notifications */}
          {error && (
            <div className="fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 bg-red-500 text-white" style={{ animation: 'slideInRight 0.5s ease-out' }}>
              <AlertCircle className="w-5 h-5" />
              <span className="font-[poppins]">{error}</span>
              <button onClick={() => setError(null)} className="ml-2 hover:opacity-80">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {success && (
            <div className="fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 bg-green-500 text-white" style={{ animation: 'slideInRight 0.5s ease-out' }}>
              <CheckCircle className="w-5 h-5" />
              <span className="font-[poppins]">{success}</span>
              <button onClick={() => setSuccess(null)} className="ml-2 hover:opacity-80">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
            {/* Management: back-to-schools navigation */}
            {isManagement && selectedSchool && (
              <button
                onClick={() => setSelectedSchool(null)}
                className="flex items-center gap-2 text-[#D67C2C] hover:underline mb-4 font-medium"
              >
                <ChevronLeft className="w-4 h-4" /> Back to All Schools
              </button>
            )}
            <div className="bg-gradient-to-r from-orange-50 to-blue-50 rounded-2xl p-8 border border-orange-100">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#D67C2C] to-orange-600 flex items-center justify-center shadow-lg">
                    <ClipboardList className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <h1 className="text-3xl font-bold text-gray-900">
                      Holistic Progress Card Management
                    </h1>
                    {isManagement && selectedSchool && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold bg-orange-100 text-[#D67C2C] border border-orange-200">
                        <GraduationCap className="w-4 h-4" />
                        {selectedSchool.school_name}
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 text-lg mb-4">
                    {isManagement && !selectedSchool
                      ? 'Monitor HPC progress across all schools in your group'
                      : 'Create, manage, and track student progress reports across all educational stages'}
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-700">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <span>View class and student data</span>
                    </div>
                    {!isManagement && (
                      <div className="flex items-center gap-2 text-gray-700">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <span>Generate official reports</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-gray-700">
                      <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                      <span>Track coverage and analytics</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation — hidden for management when no school is selected */}
          {!(isManagement && !selectedSchool) && (
            <div className="mb-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
                <div className="flex flex-wrap gap-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => {
                        setActiveTab(tab.key);
                        // Reset drill-down state when switching tabs
                        setSelectedClass(null);
                        setClassStudents([]);
                        setSelectedStudent(null);
                        setActivities([]);
                        setSelectedActivities([]);
                        setActivityStageFilter('');
                        setActivityDomainFilter('');
                        setActivityClassFilter('');
                        setViewingAssignment(null);
                        setAssignmentSubmissions([]);
                        setViewingSubmission(null);
                      }}
                      className={`flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                        activeTab === tab.key
                          ? 'bg-gradient-to-r from-[#D67C2C] to-orange-600 text-white shadow-md transform scale-105'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      {tab.icon}
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-[#D67C2C] animate-spin" />
              <span className="ml-3 text-gray-600 font-medium">Loading...</span>
            </div>
          )}

          {/* Tab Content */}
          {!loading && (
            <>
              {/* Management: schools overview when no school is selected */}
              {isManagement && !selectedSchool && renderManagementSchools()}

              {/* Normal tabs — shown for admin, or for management once a school is selected */}
              {!(isManagement && !selectedSchool) && (
                <>
                  {/* Global Assignment Detail Overlay — accessible from any tab */}
                  {viewingAssignment && !selectedStudent && !selectedClass && (
                    <div>
                      <button
                        onClick={handleBackFromAssignment}
                        className="flex items-center gap-2 text-[#D67C2C] hover:underline mb-4 font-medium"
                      >
                        <ChevronLeft className="w-4 h-4" /> Back
                      </button>
                      {viewingSubmission ? (
                        renderSubmissionDetail()
                      ) : assignmentSubmissions.length > 0 ? (
                        renderSubmissionsList()
                      ) : (
                        renderAssignmentDetail()
                      )}
                    </div>
                  )}
                  {/* Normal tab content — hidden when global assignment detail is open */}
                  {!(viewingAssignment && !selectedStudent && !selectedClass) && (
                    <>
                      {activeTab === 'overview' && renderOverview()}
                      {activeTab === 'teachers' && renderTeachers()}
                      {activeTab === 'classes' && renderClasses()}
                      {activeTab === 'students' && renderStudents()}
                      {activeTab === 'reports' && renderReports()}
                      {activeTab === 'branding' && renderBranding()}
                      {activeTab === 'coverage' && renderCoverage()}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* ─── Bulk Generation Progress Modal ────────────────────────────────── */}
      {isBulkModalOpen && bulkProgress && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Generating Class Reports</h2>
                <p className="text-sm text-gray-500 mt-0.5">{bulkProgress.class_name}</p>
              </div>
              <button
                onClick={handleCloseBulkModal}
                disabled={isBulkGenerating}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Progress bar */}
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-600 flex items-center gap-2">
                  {bulkProgress.status === 'done' ? 'Complete' : (
                    <><RefreshCw className="w-3 h-3 animate-spin" /> Generating...</>
                  )}
                </span>
                <span className="font-medium text-gray-900">
                  {(bulkProgress.completed || 0) + (bulkProgress.failed || 0)} / {bulkProgress.total}
                </span>
              </div>
              {(() => {
                const done = (bulkProgress.completed || 0) + (bulkProgress.failed || 0);
                const pct = bulkProgress.total > 0 ? (done / bulkProgress.total) * 100 : 0;
                const isInProgress = bulkProgress.status !== 'done';
                return (
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    {isInProgress && pct === 0 ? (
                      <div className="h-3 rounded-full bg-indigo-400 animate-pulse" style={{ width: '100%' }} />
                    ) : (
                      <div
                        className={`h-3 rounded-full transition-all duration-500 ${bulkProgress.status === 'done' ? 'bg-green-500' : 'bg-indigo-600'}`}
                        style={{ width: `${pct}%` }}
                      />
                    )}
                  </div>
                );
              })()}
              {bulkProgress.status === 'done' && (
                <div className="flex gap-4 mt-2 text-sm">
                  <span className="text-green-600 font-medium">✓ {bulkProgress.completed} succeeded</span>
                  {bulkProgress.failed > 0 && (
                    <span className="text-red-600 font-medium">✗ {bulkProgress.failed} failed</span>
                  )}
                </div>
              )}
            </div>

            {/* Student list */}
            <div className="flex-1 overflow-y-auto p-4 space-y-1">
              {/* Pending students not yet in results */}
              {Array.from({ length: Math.max(0, bulkProgress.total - (bulkProgress.results?.length || 0)) }).map((_, i) => (
                <div key={`pending-${i}`} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50">
                  <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
                  <span className="text-sm text-gray-400">Pending...</span>
                </div>
              ))}
              {/* Completed results (show in reverse order so latest is on top) */}
              {[...(bulkProgress.results || [])].reverse().map((r, i) => (
                <div
                  key={r.student_id || i}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg ${r.status === 'success' ? 'bg-green-50' : 'bg-red-50'}`}
                >
                  {r.status === 'success' ? (
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  )}
                  <span className="text-sm text-gray-800 flex-1">{r.student_name}</span>
                  {r.status === 'failed' && r.error && (
                    <span className="text-xs text-red-500 max-w-[150px] truncate" title={r.error}>{r.error}</span>
                  )}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={handleCloseBulkModal}
                disabled={isBulkGenerating}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-sm transition-colors"
              >
                {isBulkGenerating ? 'Please wait...' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default HPCManagement;
